import { useCallback } from 'react';
import { Column } from './types';

// Drag and Drop types
export type DragItem = {
  columnKey: string;
  index: number;
  sourceGroup: 'leftPinned' | 'regular' | 'rightPinned';
};

export type DropResult = {
  targetIndex: number;
  targetGroup: 'leftPinned' | 'regular' | 'rightPinned';
};

// Column reordering hook
export type ColumnReorderResult = {
  moveColumn: (dragItem: DragItem, dropResult: DropResult) => void;
};

export const useColumnReorder = (
  columns: Column[],
  pinnedLeft: string[],
  pinnedRight: string[],
  onColumnsChange: (columns: Column[]) => void,
  setPinnedLeft: (columns: string[]) => void,
  setPinnedRight: (columns: string[]) => void
): ColumnReorderResult => {
  const moveColumn = useCallback(
    (dragItem: DragItem, dropResult: DropResult) => {
      const { columnKey, sourceGroup } = dragItem;
      const { targetIndex, targetGroup } = dropResult;

      // Get current column order for each group
      const currentLeftPinned = [...pinnedLeft];
      const currentRightPinned = [...pinnedRight];
      const currentRegular = columns
        .filter(
          (col) =>
            !pinnedLeft.includes(col.key) && !pinnedRight.includes(col.key)
        )
        .map((col) => col.key);

      // Remove from source group
      let newLeftPinned = [...currentLeftPinned];
      let newRightPinned = [...currentRightPinned];
      let newRegular = [...currentRegular];

      if (sourceGroup === 'leftPinned') {
        newLeftPinned = newLeftPinned.filter((key) => key !== columnKey);
      } else if (sourceGroup === 'rightPinned') {
        newRightPinned = newRightPinned.filter((key) => key !== columnKey);
      } else {
        newRegular = newRegular.filter((key) => key !== columnKey);
      }

      // Add to target group
      if (targetGroup === 'leftPinned') {
        newLeftPinned.splice(targetIndex, 0, columnKey);
      } else if (targetGroup === 'rightPinned') {
        newRightPinned.splice(targetIndex, 0, columnKey);
      } else {
        newRegular.splice(targetIndex, 0, columnKey);
      }

      // Update pinned columns
      setPinnedLeft(newLeftPinned);
      setPinnedRight(newRightPinned);

      // Reorder columns array based on new order
      const newColumnOrder = [
        ...newLeftPinned,
        ...newRegular,
        ...newRightPinned,
      ];
      const newColumns = newColumnOrder
        .map((key) => columns.find((col) => col.key === key)!)
        .filter(Boolean);

      onColumnsChange(newColumns);
    },
    [
      columns,
      pinnedLeft,
      pinnedRight,
      onColumnsChange,
      setPinnedLeft,
      setPinnedRight,
    ]
  );

  return {
    moveColumn,
  };
};
